import React, { useEffect, useState } from 'react';
import { Alert, Button, Space, message, Form, Input, InputNumber, Switch, Card, Select } from 'antd';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import { useTheme } from '../contexts/ThemeContext';
import { systemConfigApi, dictionariesApi } from '../services/api-services';

const SystemConfigManagement: React.FC = () => {
  const [config, setConfig] = useState<any>({});
  const [content, setContent] = useState('');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { theme } = useTheme();

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res: any = editing ? await systemConfigApi.getAllConfigs() : await systemConfigApi.getConfig();
      const next = res || {};
      setConfig(next);
      setContent(JSON.stringify(next, null, 2));
    } catch (e: any) {
      message.error(e.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, []);

  const parseContent = () => {
    let parsed: any;
    try {
      parsed = JSON.parse(content);
    } catch {
      message.error('JSON 格式错误，请检查后再保存');
      return null;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      message.error('系统配置必须是 JSON 对象');
      return null;
    }
    return parsed;
  };

  const saveConfig = async (next: any) => {
    setSaving(true);
    try {
      await systemConfigApi.updateConfig(next);
      setConfig(next);
      setContent(JSON.stringify(next, null, 2));
      message.success('保存成功');
    } catch (e: any) {
      message.error(e.response?.data?.detail || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleJsonSave = async () => {
    const parsed = parseContent();
    if (!parsed) return;
    await saveConfig(parsed);
    setEditing(false);
  };

  const handleFormat = () => {
    const parsed = parseContent();
    if (!parsed) return;
    setContent(JSON.stringify(parsed, null, 2));
  };

  const handleFormSave = async () => {
    await saveConfig(config);
  };

  const email = config.email || {};
  const tencentSms = config.tencent_sms || {};
  const [acpBackendOptions, setAcpBackendOptions] = useState<{ label: string; value: string }[]>([
    { value: 'hermes', label: 'Hermes' },
    { value: 'kiro', label: 'Kiro CLI' },
    { value: 'copilot', label: 'GitHub Copilot' },
  ]);
  useEffect(() => {
    dictionariesApi.getByCode('acp_backend').then((dict: any) => {
      if (dict?.items) setAcpBackendOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    }).catch(() => {});
  }, []);

  const setEmail = (key: string, value: any) =>
    setConfig((prev: any) => ({ ...prev, email: { ...(prev.email || {}), [key]: value } }));
  const setTencentSms = (key: string, value: any) =>
    setConfig((prev: any) => ({ ...prev, tencent_sms: { ...(prev.tencent_sms || {}), [key]: value } }));

  return (
    <div className="apple-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <div>
          <h1 className="apple-page-title" style={{ marginBottom: 4 }}>基础配置</h1>
        </div>
        <Space>
          {editing ? (
            <>
              <Button onClick={() => { setEditing(false); }}>关闭</Button>
            </>
          ) : (
            <>
              <Button onClick={async () => { 
                setEditing(true);
                setLoading(true);
                try {
                  const allConfig: any = await systemConfigApi.getAllConfigs();
                  setContent(JSON.stringify(allConfig, null, 2));
                } catch (e: any) {
                  message.error('加载配置失败');
                } finally {
                  setLoading(false);
                }
              }}>查看JSON配置</Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={handleFormSave} loading={saving}>保存</Button>
            </>
          )}
        </Space>
      </div>
      {editing ? (
        <>
          <Alert
            type="warning"
            showIcon
            message="该配置可能包含 Token 等敏感信息，仅超级管理员可编辑。"
            style={{ marginBottom: 16 }}
          />
          <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
            <Editor
              height="calc(100vh - 260px)"
              language="json"
              theme={theme === 'glass' ? 'vs-dark' : 'light'}
              value={content}
              onChange={(val) => setContent(val || '')}
              options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, padding: { top: 12 } }}
              loading={loading ? '加载中...' : undefined}
            />
          </div>
        </>
      ) : (
        <div style={{ maxWidth: 720 }}>
          <Card title="邮箱发送配置" size="small" style={{ marginBottom: 16 }}>
            <Form layout="vertical">
              <Form.Item label="SMTP 服务器" tooltip="如 smtp.qq.com、smtp.163.com">
                <Input
                  value={email.smtp_host || ''}
                  placeholder="smtp.example.com"
                  onChange={(e) => setEmail('smtp_host', e.target.value)}
                />
              </Form.Item>
              <Space size="large" wrap>
                <Form.Item label="端口" style={{ marginBottom: 0 }}>
                  <InputNumber
                    value={email.smtp_port ?? 465}
                    min={1}
                    max={65535}
                    placeholder="465"
                    style={{ width: 160 }}
                    onChange={(v) => setEmail('smtp_port', v)}
                  />
                </Form.Item>
                <Form.Item label="使用 SSL" style={{ marginBottom: 0 }}>
                  <Switch
                    checked={email.use_ssl ?? true}
                    onChange={(v) => setEmail('use_ssl', v)}
                  />
                </Form.Item>
              </Space>
              <Form.Item label="发件邮箱（账号）" style={{ marginTop: 16 }}>
                <Input
                  value={email.sender || ''}
                  placeholder="noreply@example.com"
                  onChange={(e) => setEmail('sender', e.target.value)}
                />
              </Form.Item>
              <Form.Item label="密码 / 授权码">
                <Input.Password
                  value={email.password || ''}
                  placeholder="SMTP 授权码"
                  autoComplete="new-password"
                  onChange={(e) => setEmail('password', e.target.value)}
                />
              </Form.Item>
              <Form.Item label="发件人名称" tooltip="收件人看到的发件人显示名，可选">
                <Input
                  value={email.sender_name || ''}
                  placeholder="uzyClaw"
                  onChange={(e) => setEmail('sender_name', e.target.value)}
                />
              </Form.Item>
            </Form>
          </Card>

          <Card title="短信发送配置（腾讯云）" size="small">
            <Form layout="vertical">
              <Form.Item label="Secret ID" tooltip="腾讯云 API 密钥 ID">
                <Input
                  value={tencentSms.secret_id || ''}
                  placeholder="AKIDxxxxxxxxxx"
                  onChange={(e) => setTencentSms('secret_id', e.target.value)}
                />
              </Form.Item>
              <Form.Item label="Secret Key" tooltip="腾讯云 API 密钥 Key">
                <Input.Password
                  value={tencentSms.secret_key || ''}
                  placeholder="Secret Key"
                  autoComplete="new-password"
                  onChange={(e) => setTencentSms('secret_key', e.target.value)}
                />
              </Form.Item>
              <Form.Item label="SDK App ID" tooltip="短信应用的 SDK AppID">
                <Input
                  value={tencentSms.sdk_app_id || ''}
                  placeholder="1400000000"
                  onChange={(e) => setTencentSms('sdk_app_id', e.target.value)}
                />
              </Form.Item>
              <Form.Item label="签名名称（Sign Name）" tooltip="已审核通过的短信签名内容">
                <Input
                  value={tencentSms.sign_name || ''}
                  placeholder="短信签名内容"
                  onChange={(e) => setTencentSms('sign_name', e.target.value)}
                />
              </Form.Item>
            </Form>
          </Card>

          <Card title="默认 ACP 后端" size="small" style={{ marginTop: 16 }}>
            <Form layout="vertical">
              <Form.Item label="默认 ACP 后端" tooltip="未选择数字人时使用的 ACP 智能体后端">
                <Select
                  value={config.default_acp_backend || 'hermes'}
                  onChange={(v) => setConfig((prev: any) => ({ ...prev, default_acp_backend: v }))}
                  options={acpBackendOptions}
                />
              </Form.Item>
            </Form>
          </Card>

          <Card title="配置访问 Token" size="small" style={{ marginTop: 16 }}>
            <Form layout="vertical">
              <Form.Item label="Access Token" tooltip="设置后可通过 Header X-Config-Token 无需登录获取配置">
                <Space.Compact style={{ width: '100%' }}>
                  <Input.Password
                    value={config.config_access_token || ''}
                    placeholder="设置配置访问 Token"
                    autoComplete="new-password"
                    onChange={(e) => setConfig((prev: any) => ({ ...prev, config_access_token: e.target.value }))}
                  />
                  <Button onClick={() => setConfig((prev: any) => ({ ...prev, config_access_token: crypto.randomUUID().replace(/-/g, '') }))}>随机</Button>
                </Space.Compact>
              </Form.Item>
              <div style={{ color: 'var(--apple-text-secondary)', fontSize: 12 }}>
                设置后可无需登录获取配置：<br />
                <code>GET /api/v1/system-configs?token=your_token</code><br />
                携带 <code>config_id</code> 参数可判断配置是否变更，未变更时返回 <code>{`{"changed": false}`}</code><br />
                <code>GET /api/v1/system-configs?token=your_token&config_id=xxx</code><br />
                参数也支持 Header 方式：<code>X-Config-Token</code>、<code>X-Config-Id</code>
              </div>
            </Form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SystemConfigManagement;
